//Function to  map through usgs unit types
import { usgsGetAllValuesForParam } from 'features/Integrations/features/ConnectedDevices/utils/usgsData.utils';
import { buildColorMap } from './deviceGraphHelpers.utils';
import { USGSMeasureNormalizer } from './measurement.utils';
// empty chart model for default return
const emptyChart = { labels: [], datasets: [] };

/**
 * Check if a device is USGS Monitoring Station
 *
 * @param   {object}  device  [the full device object]
 *
 * @returns  {boolean}          [true if the device is a USGS Monitoring Station, false otherwise]
 */
export const isUSGSDevice = (device) => {
  return device?.system_type === 'USGS' && device.device_type === 'Monitoring Station';
};

/**
 * This function is used to build the USGS Device Chart Model for the USGS Device Graph Component
 *
 * It will call various helper functions to build the datasets and labels for the chart
 *
 * @param   {object}  params
 * @param   {[array]}  params.devices              [Array of all devices]
 * @param   {object}  params.selectedDeviceMap    [Map of selected device ids to boolean]
 * @param   {string}  params.selectedDataType     [The selected data type to display on the graph]
 * @param   {boolean} params.unitType             [Whether to convert units or not]
 * @param   {number}  params.startTime            [Start time for the graph data range]
 * @param   {number}  params.endTime              [End time for the graph data range]
 *
 * @return  {[label, datasets]}  [the chart model containing labels and datasets for the series]
 */
export const buildUSGSDeviceChartModel = ({
  devices = [],
  selectedDeviceMap = {}, // { [id]: boolean }
  selectedDataType = 'Gage height',
  unitType = false,
  startTime,
  endTime,
}) => {
  if (!devices || devices.length === 0 || !startTime || !endTime) {
    return emptyChart;
  }

  const usgsDevices = getUSGSDevices(devices);
  const usgsDeviceMap = buildUSGSDeviceMap(usgsDevices);

  if (usgsDevices.length === 0 || usgsDeviceMap.size === 0) {
    return emptyChart;
  }

  const colors = buildColorMap(usgsDevices); // Contains all colors for devices

  const filterWithinTimeRange = (dataPoint) => {
    if (!dataPoint || dataPoint.ts == null) return false;
    const d = new Date(dataPoint.ts);
    return d >= startTime && d <= endTime;
  };

  const datasets = buildUSGSDatasets(
    usgsDeviceMap,
    selectedDeviceMap,
    filterWithinTimeRange,
    selectedDataType,
    colors,
    unitType,
  );

  if (!datasets.length) {
    return emptyChart;
  }
  // datasets = points to plot with meta info (colors and etc)
  // labels is empty — ECharts time axis generates ticks automatically from min/max
  return { labels: [], datasets };
};

export const getUSGSDevices = (devices) => {
  const usgsDevices = devices.filter((device) => isUSGSDevice(device));
  return usgsDevices;
};

// Return sorted, deduplicated parameter names for the selected USGS devices
export const getUSGSParameterNamesForSelected = (devices, selectedDeviceMap, unitType = false) => {
  const usgsDevices = getUSGSDevices(devices) || [];
  const selectedDevices = selectedDeviceMap
    ? usgsDevices.filter((device) => selectedDeviceMap?.[String(device.id)])
    : [];

  // Build a map of parameter name -> unit (take first available unit for that parameter)
  const paramUnitMap = new Map();
  selectedDevices.forEach((device) => {
    //Use time_series field for parameter/unit info if available
    if (Array.isArray(device.time_series)) {
      device.time_series.forEach((ts) => {
        const tsName = ts && ts.parameter_name;
        if (!tsName) return;
        if (!paramUnitMap.has(tsName)) {
          const normalized = USGSMeasureNormalizer(0, ts.unit_of_measure);
          const unit =
            unitType && normalized?.alt_unit ? normalized.alt_unit : normalized?.unit || null;
          paramUnitMap.set(tsName, unit);
        }
      });
    }
  });

  // Return array of objects { name, unit } sorted by name
  return [...paramUnitMap.entries()]
    .map(([name, unit]) => ({ name, unit }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const buildUSGSDeviceMap = (usgsDevices) => {
  const deviceMap = new Map();
  usgsDevices.forEach((device) => {
    // Key by stringified id to align with selectedDeviceMap keys
    if (device && device.id != null) {
      deviceMap.set(String(device.id), device);
    }
  });
  return deviceMap;
};

/**
 * Graph-specific helper: filter a device's `data` for datapoints that contain
 * values for the selected parameter name. Assumes datapoints follow the new
 * `values` map shape. Returns { matchingTSIds, filteredData }.
 */
const usgsGetFilteredDataForParameter = (device, selectedDataType, filterWithinTimeRange) => {
  const { parameter, values } = usgsGetAllValuesForParam(device, selectedDataType) || {};

  // If no parameter found, return empty result
  if (!parameter) return { matchingTSIds: new Set(), filteredData: [] };

  const matchingTSIds = new Set([parameter.id]);

  // values is an array of { ts, value, qualifier, approval_status, did, uid }
  const filteredData = (values || [])
    .filter((dataPoint) => dataPoint && filterWithinTimeRange({ ts: dataPoint.ts }))
    .map((v) => {
      const normalized = USGSMeasureNormalizer(v.value, parameter.unit_of_measure);
      return {
        ts: v.ts,
        did: v.did,
        uid: v.uid,
        // expose top-level value/unit for tooltip and label discovery
        value: v.value,
        unit_of_measure: normalized?.unit ?? null,
        // Include alt_value/unit for unit toggling in the graph
        alt_value: normalized?.alt_value ?? null,
        alt_unit: normalized?.alt_unit ?? null,
        parameter_name: parameter.parameter_name ?? null,
        // normalize into the ``values`` map shape the graph expects
        values: {
          [parameter.id]: {
            value: v.value,
            qualifier: v.qualifier ?? null,
            approval_status: v.approval_status ?? null,
          },
        },
      };
    })
    .sort((a, b) => a.ts - b.ts);

  return { matchingTSIds, filteredData };
};

/**
 * Build chart datasets for selected USGS devices.
 *
 * Produces an array of dataset objects suitable for the charting utilities in
 * this feature. Each dataset contains a `_meta` payload (original device with
 * filtered `data`), a `label`, `data` as an array of `[timestamp, value]`
 * tuples, and a `color` keyed by device id.
 *
 * @param {Map<string, import('../types/UsgsTypes').USGSDevice>} usgsDeviceMap - Map of device alias -> device object
 * @param {Record<string, boolean>} selectedDeviceMap - Map of alias -> selected (truthy) flag
 * @param {(dataPoint: import('../types/UsgsTypes').GraphDataPoint) => boolean} filterWithinTimeRange - Predicate to filter datapoints by time range
 * @param {string} selectedDataType - Selected parameter name to display (e.g., 'Gage Height')
 * @param {Record<number|string, string>} colors - Map of device id -> color string used for styling
 * @param {boolean} unitType - Unit selection flag (true => convert units)
 * @returns {import('../types/UsgsTypes').GraphDataset[]} Array of chart dataset objects (each may include `_meta` with device and filtered data)
 */
const buildUSGSDatasets = (
  usgsDeviceMap,
  selectedDeviceMap,
  filterWithinTimeRange,
  selectedDataType,
  colors,
  unitType,
) => {
  const ids = Array.from(usgsDeviceMap.keys());
  const results = [];
  ids.forEach((deviceId) => {
    // Skip if the id is not selected
    if (!selectedDeviceMap || !selectedDeviceMap[deviceId]) return;

    const device = usgsDeviceMap.get(String(deviceId));
    if (!device) return;

    const dataset = buildChartDatasetForDevice(
      device,
      selectedDataType,
      filterWithinTimeRange,
      colors,
      unitType,
    );

    if (dataset) results.push(dataset);
  });

  return results.reverse();
};

/**
 * Build a single chart dataset object for a device and selected parameter.
 * Returns `null` when no datapoints match.
 */
const buildChartDatasetForDevice = (
  device,
  selectedDataType,
  filterWithinTimeRange,
  colors,
  unitType,
) => {
  if (!device) return null;

  const { matchingTSIds, filteredData } = usgsGetFilteredDataForParameter(
    device,
    selectedDataType,
    filterWithinTimeRange,
  );

  if (!filteredData || filteredData.length === 0) return null;

  const datapoints = filteredData
    .map((deviceData) => {
      const timestamp = deviceData.ts;
      const yValue = getUSGSYAxisValue(deviceData, selectedDataType, unitType, device.time_series);
      return [timestamp, yValue];
    })
    .filter((dataPoint) => dataPoint[1] !== null && dataPoint[1] !== undefined);

  if (!datapoints.length) return null;

  const label = device.alias || `${device.alias}`;
  return {
    _meta: { ...device, data: filteredData },
    label,
    data: datapoints,
    color: colors[device.id],
  };
};

/**
 * [getUSGSYAxisValue description]
 *
 * @param   {[import('../../../types').UsgsDataPoint][]}  deviceData        [Array of USGS data points | this will be in the device.data field]
 * @param   {[string]}  selectedDataType  [The parameter name to look for in the data points]
 * @param   {[boolean]}  unitType          [Unit selection flag (true => convert units)]
 * @param   {[import('../../../types').USGSTimeSeries][]} timeSeries       [Array of time series metadata objects from the device, used for unit conversion if needed]
 *
 * @return  {[number|null]}                    [The Y-axis value for the chart, or null if not applicable]
 */
const getUSGSYAxisValue = (deviceData, selectedDataType, unitType, timeSeries = []) => {
  if (!deviceData) return null;

  // Support both dp.values map and direct time-series keys on the datapoint
  const valuesMap =
    deviceData.values && typeof deviceData.values === 'object' ? deviceData.values : deviceData;

  // Find a matching timeseries entry in the datapoint's values map
  for (const [timeSeriesID, point] of Object.entries(valuesMap)) {
    if (!point || point.value == null) continue;
    const meta = Array.isArray(timeSeries)
      ? timeSeries.find((ts) => ts && ts.id === timeSeriesID)
      : null;
    const paramName = meta?.parameter_name ?? null;
    if (!paramName || paramName !== selectedDataType) continue;

    const value = Number(point.value);
    if (!isFinite(value)) return null;

    if (!unitType) return value;
    return deviceData.alt_value ?? value;
  }

  return null;
};
